/**
 * ResortQuickCompare — Tabella comparativa rapida dei resort HCF Property
 * mostrata sulla landing pubblica.
 *
 * ★ 2026-08-13 (Fase 51) — Su richiesta utente: colonne
 *   Resort · Paese · Prezzo da · Consegna · Piano pagamenti · Rendimento indicativo
 *   → il visitatore capisce in 10 secondi quale progetto fa per lui, senza dover
 *   cliccare ogni pagina resort.
 *
 * NB "Rendimento indicativo" è sempre marcato come proiezione non garantita.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ArrowRight, MapPin, Calendar, Home, TrendingUp, Wallet } from 'lucide-react';
import { RESORT_FINANCIALS, fmtEUR } from '../lib/financial';

// ★ 2026-08-28 — Rimossi Lombok, Chumphon e Spille (eliminati dall'ecosistema IT).
// Attualmente solo La Mar Residence + Narea sono attivi (Real Estate Albania).
const ROWS = [
  {
    slug: 'la-mar-residence',
    label: 'La Mar Residence',
    country: 'Albania',
    countryEmoji: '🇦🇱',
    delivery: '2027',
    plan: 'Caparra + 4 rate + saldo',
    yieldNote: '~5% netto/anno (proiezione indicativa)',
    fitFor: 'Ticket accessibile, bilocale fronte mare',
    priorityBadge: null,
  },
  {
    slug: 'narea',
    label: 'Narea',
    country: 'Albania',
    countryEmoji: '🇦🇱',
    delivery: '2029',
    plan: 'Caparra + anticipo 40% + 5 rate + saldo',
    yieldNote: '~5% netto/anno (proiezione indicativa)',
    fitFor: 'Landmark 14 piani, 161 unità',
    priorityBadge: null,
  },
  // ★ 2026-08-28 — Palm Paradise (Querret · 198 unità · 4 blocchi · Q2 2027)
  {
    slug: 'palm-paradise',
    label: 'Palm Paradise',
    country: 'Albania',
    countryEmoji: '🇦🇱',
    delivery: 'Q2 2027',
    plan: 'Caparra €5.000 + anticipo + rata + saldo',
    yieldNote: '~5% netto/anno (proiezione indicativa)',
    fitFor: 'Quartiere tropicale, 4 blocchi, 198 unità',
    priorityBadge: null,
  },
  // ★ 2026-08-29 — Vista Mare (Querret · 189 unità · Aprile 2027)
  {
    slug: 'vista-mare',
    label: 'Vista Mare',
    country: 'Albania',
    countryEmoji: '🇦🇱',
    delivery: 'Aprile 2027',
    plan: 'Caparra 3% + anticipo 30% + 5 rate + saldo',
    yieldNote: '~5% netto/anno (proiezione indicativa)',
    fitFor: 'Vista Adriatico, ticket accessibile, 189 unità',
    priorityBadge: null,
  },
  // ★ 2026-08-29 — Green Paradise Lombok (Bumbang Bay · 32 ville · Ottobre 2027)
  {
    slug: 'green-paradise-lombok',
    label: 'Green Paradise Lombok',
    country: 'Indonesia',
    countryEmoji: '🇮🇩',
    delivery: 'Ottobre 2027',
    plan: 'Caparra + anticipo + 5 rate + saldo',
    yieldNote: '~15% netto/anno (proiezione indicativa)',
    fitFor: 'Ville private in resort eco-luxury, 32 unità',
    priorityBadge: null,
  },
];

function PriceCell({ slug }) {
  const fin = RESORT_FINANCIALS[slug];
  if (!fin) return <span className="text-muted-foreground">—</span>;
  if (fin.realEstate?.soldOut) {
    return <span className="text-red-600 font-medium">SOLD OUT</span>;
  }
  const from = fin.realEstate?.startingFrom;
  const promo = fin.promo?.active ? fin.promo.discountPct : null;
  return (
    <div className="flex flex-col">
      <span className="font-serif text-lg text-[hsl(var(--primary))]">{from ? fmtEUR(from) : '—'}</span>
      {promo && (
        <span className="text-[10px] text-[var(--gp-gold)] font-medium">-{promo}% pre-launch</span>
      )}
    </div>
  );
}

function PriorityBadge({ kind }) {
  const map = {
    'nuovo':     { label: 'In vendita', cls: 'bg-emerald-500/15 text-emerald-700 border-emerald-500/30' },
    'promo':     { label: 'Promo -30%', cls: 'bg-[var(--gp-gold)]/15 text-[var(--gp-gold)] border-[var(--gp-gold)]/30' },
    'sold-out':  { label: 'Sold Out', cls: 'bg-red-500/15 text-red-700 border-red-500/30' },
  };
  const c = map[kind];
  if (!c) return null;
  return <Badge variant="outline" className={`text-[10px] py-0 h-4 ml-1.5 ${c.cls}`}>{c.label}</Badge>;
}

export function ResortQuickCompare() {
  return (
    <section
      className="px-4 md:px-12 py-12 md:py-20 max-w-6xl mx-auto"
      data-testid="resort-quick-compare"
    >
      <div className="text-center max-w-2xl mx-auto mb-8 md:mb-10">
        <div className="text-[10px] md:text-xs tracking-[0.3em] uppercase text-muted-foreground">Confronto rapido</div>
        <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-[hsl(var(--primary))] mt-2">
          Quale resort fa per te?
        </h2>
        <p className="text-sm text-muted-foreground mt-3">
          Prezzo di ingresso, timing di consegna, piano pagamenti e proiezioni indicative
          per ogni progetto — tutto in una tabella, senza dover cliccare 5 pagine.
        </p>
      </div>

      {/* Desktop table */}
      <Card className="hidden md:block overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[var(--surface-2)]/60">
              <tr className="text-left">
                <th className="py-3 px-4 text-[10px] uppercase tracking-widest text-muted-foreground">Resort</th>
                <th className="py-3 px-4 text-[10px] uppercase tracking-widest text-muted-foreground">Paese</th>
                <th className="py-3 px-4 text-[10px] uppercase tracking-widest text-muted-foreground">Prezzo da</th>
                <th className="py-3 px-4 text-[10px] uppercase tracking-widest text-muted-foreground">Consegna</th>
                <th className="py-3 px-4 text-[10px] uppercase tracking-widest text-muted-foreground">Piano pagamenti</th>
                <th className="py-3 px-4 text-[10px] uppercase tracking-widest text-muted-foreground">Rendimento indicativo</th>
                <th className="py-3 px-4 text-[10px] uppercase tracking-widest text-muted-foreground"></th>
              </tr>
            </thead>
            <tbody>
              {ROWS.map((row) => {
                const fin = RESORT_FINANCIALS[row.slug];
                const soldOut = fin?.realEstate?.soldOut;
                return (
                  <tr
                    key={row.slug}
                    className={`border-t hover:bg-[var(--surface-1)]/50 transition-colors ${soldOut ? 'opacity-70' : ''}`}
                    data-testid={`compare-row-${row.slug}`}
                  >
                    <td className="py-3.5 px-4">
                      <div className="flex items-center gap-1.5">
                        <div>
                          <div className="font-medium text-foreground leading-tight">{row.label}</div>
                          <div className="text-[11px] text-muted-foreground mt-0.5">{row.fitFor}</div>
                        </div>
                        <PriorityBadge kind={row.priorityBadge} />
                      </div>
                    </td>
                    <td className="py-3.5 px-4 whitespace-nowrap">
                      <div className="inline-flex items-center gap-1.5 text-xs">
                        <span className="text-base">{row.countryEmoji}</span>
                        <span>{row.country}</span>
                      </div>
                    </td>
                    <td className="py-3.5 px-4">
                      <PriceCell slug={row.slug} />
                    </td>
                    <td className="py-3.5 px-4 whitespace-nowrap">
                      <div className="inline-flex items-center gap-1.5 text-xs">
                        <Calendar className="w-3 h-3 text-muted-foreground" />
                        {row.delivery}
                      </div>
                    </td>
                    <td className="py-3.5 px-4 text-xs text-muted-foreground">{row.plan}</td>
                    <td className="py-3.5 px-4 text-xs">
                      <div className="inline-flex items-center gap-1 text-muted-foreground">
                        <TrendingUp className="w-3 h-3" />
                        {row.yieldNote}
                      </div>
                    </td>
                    <td className="py-3.5 px-4 text-right">
                      <Link to={`/resort/${row.slug}`}>
                        <Button size="sm" variant="outline" className="h-8 text-xs" data-testid={`compare-cta-${row.slug}`}>
                          Scopri
                          <ArrowRight className="w-3 h-3 ml-1" />
                        </Button>
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Mobile cards */}
      <div className="md:hidden space-y-3">
        {ROWS.map((row) => {
          const fin = RESORT_FINANCIALS[row.slug];
          const soldOut = fin?.realEstate?.soldOut;
          return (
            <Card key={row.slug} className={`p-4 ${soldOut ? 'opacity-70' : ''}`} data-testid={`compare-card-${row.slug}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-1.5">
                    <span className="font-serif text-lg text-[hsl(var(--primary))] leading-tight">{row.label}</span>
                    <PriorityBadge kind={row.priorityBadge} />
                  </div>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground mt-0.5">
                    <MapPin className="w-3 h-3" />
                    <span className="text-sm">{row.countryEmoji}</span> {row.country}
                  </div>
                </div>
                <PriceCell slug={row.slug} />
              </div>
              <div className="grid grid-cols-2 gap-2 mt-3 text-[11px] text-muted-foreground">
                <div className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {row.delivery}</div>
                <div className="flex items-center gap-1"><Home className="w-3 h-3" /> {row.fitFor}</div>
                <div className="flex items-center gap-1 col-span-2"><Wallet className="w-3 h-3" /> {row.plan}</div>
                <div className="flex items-center gap-1 col-span-2"><TrendingUp className="w-3 h-3" /> {row.yieldNote}</div>
              </div>
              <Link to={`/resort/${row.slug}`} className="block mt-3">
                <Button size="sm" variant="outline" className="w-full h-8 text-xs">
                  Scopri {row.label.replace('Green Paradise ', '')}
                  <ArrowRight className="w-3 h-3 ml-1" />
                </Button>
              </Link>
            </Card>
          );
        })}
      </div>

      {/* Disclaimer sotto tabella */}
      <p className="mt-5 text-[10px] md:text-[11px] text-muted-foreground/80 max-w-3xl mx-auto text-center leading-relaxed">
        Le proiezioni di rendimento sono <b>indicative e non garantite</b>. Dipendono da
        occupancy, ADR, cambio valuta e gestione operativa. L&apos;acquisto avviene tramite
        contratto notarile immobiliare locale, senza intermediazione finanziaria.
      </p>
    </section>
  );
}

export default ResortQuickCompare;
